import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { fmtNumber } from "../lib/utils";
import { Filter, X, ChevronDown, ChevronUp, Database, Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "../lib/auth";

const STATUS_PILL = {
  approved: "pill-success",
  needs_review: "pill-warn",
  pending: "pill-info",
  rejected: "pill-error",
};

const WRITE_ROLES = new Set(["esg_lead", "tenant_admin", "kpi_owner", "approver", "data_contributor", "platform_admin"]);
const DELETE_ROLES = new Set(["esg_lead", "tenant_admin", "approver", "platform_admin"]);

function Empty({ msg, canWrite }) {
  return (
    <tr><td colSpan={canWrite ? 12 : 11} className="text-center text-ink-soft py-6 text-xs">{msg}</td></tr>
  );
}

export default function ActivityRecordsTable({ kpiId, compact = false, defaultPeriod = null }) {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.has(user?.role);
  const canDelete = DELETE_ROLES.has(user?.role);
  const [filters, setFilters] = useState({
    activity_type: "",
    source_entity: "",
    record_status: "",
    consumption_from: "",
    consumption_to: "",
    uploaded_from: "",
    uploaded_to: "",
    modified_from: "",
    modified_to: "",
    period: defaultPeriod || "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [catalogue, setCatalogue] = useState(null);
  const [editing, setEditing] = useState(null); // record being edited
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { kpi_id: kpiId, limit: 500 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/activity-records", { params });
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kpiId]);

  useEffect(() => {
    if (canWrite && !catalogue) {
      api.get("/activity-records/catalogue").then((r) => setCatalogue(r.data));
    }
  }, [canWrite, catalogue]);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((v) => v && v !== "").length,
    [filters]
  );

  const clearFilters = () => {
    setFilters({
      activity_type: "", source_entity: "", record_status: "",
      consumption_from: "", consumption_to: "",
      uploaded_from: "", uploaded_to: "",
      modified_from: "", modified_to: "",
      period: "",
    });
  };

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="card overflow-hidden" data-testid="activity-records-card">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="section-label flex items-center gap-2">
            <Database size={12} /> Source Data — Business Activities
          </div>
          <div className="text-xs text-ink-soft mt-0.5">
            How each business activity was converted into emissions (consumption × emission factor → tCO₂e).
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-ink-soft font-mono" data-testid="activity-records-count">
            {data ? `${data.count} rows` : "…"}
          </span>
          <button
            data-testid="toggle-activity-filters"
            onClick={() => setShowFilters((s) => !s)}
            className="btn btn-secondary"
          >
            <Filter size={12} /> Filters
            {activeFilterCount > 0 && (
              <span className="pill pill-info text-[10px] ml-1">{activeFilterCount}</span>
            )}
          </button>
          {canWrite && (
            <button
              data-testid="add-activity-record"
              onClick={() => setCreating(true)}
              className="btn btn-primary"
              disabled={!catalogue}
              title="Add a new business activity record"
            >
              <Plus size={12}/> Add record
            </button>
          )}
          <button onClick={load} className="btn btn-ghost" data-testid="activity-records-refresh">
            Refresh
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="px-4 py-3 border-b border-border bg-surface/40 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs" data-testid="activity-filters-panel">
          <div>
            <label className="dlabel">Activity type</label>
            <select
              data-testid="filter-activity-type"
              value={filters.activity_type}
              onChange={(e) => update("activity_type", e.target.value)}
              className="w-full border border-border rounded-md px-2 py-1.5 mt-1 outline-none focus:border-brand"
            >
              <option value="">All</option>
              {data?.facets?.activity_types?.map((a) => (
                <option key={a} value={a}>{a.replaceAll("_", " ")}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="dlabel">Source entity</label>
            <select
              data-testid="filter-source-entity"
              value={filters.source_entity}
              onChange={(e) => update("source_entity", e.target.value)}
              className="w-full border border-border rounded-md px-2 py-1.5 mt-1 outline-none focus:border-brand"
            >
              <option value="">All</option>
              {data?.facets?.source_entities?.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="dlabel">Record status</label>
            <select
              data-testid="filter-record-status"
              value={filters.record_status}
              onChange={(e) => update("record_status", e.target.value)}
              className="w-full border border-border rounded-md px-2 py-1.5 mt-1 outline-none focus:border-brand"
            >
              <option value="">All</option>
              {data?.facets?.record_statuses?.map((s) => (
                <option key={s} value={s}>{s.replaceAll("_", " ")}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="dlabel">Period</label>
            <input
              data-testid="filter-period"
              value={filters.period}
              onChange={(e) => update("period", e.target.value)}
              placeholder="e.g. 2025-Q4"
              className="w-full border border-border rounded-md px-2 py-1.5 mt-1 outline-none focus:border-brand"
            />
          </div>

          {/* Date ranges */}
          <DateRange label="Consumption date" from={filters.consumption_from} to={filters.consumption_to}
            onFrom={(v) => update("consumption_from", v)} onTo={(v) => update("consumption_to", v)} testidPrefix="filter-consumption" />
          <DateRange label="Upload date" from={filters.uploaded_from} to={filters.uploaded_to}
            onFrom={(v) => update("uploaded_from", v)} onTo={(v) => update("uploaded_to", v)} testidPrefix="filter-uploaded" />
          <DateRange label="Modified date" from={filters.modified_from} to={filters.modified_to}
            onFrom={(v) => update("modified_from", v)} onTo={(v) => update("modified_to", v)} testidPrefix="filter-modified" />

          <div className="col-span-2 md:col-span-4 flex justify-end gap-2">
            <button onClick={clearFilters} className="btn btn-ghost" data-testid="clear-activity-filters">
              <X size={12} /> Clear
            </button>
            <button onClick={load} className="btn btn-primary" data-testid="apply-activity-filters">
              Apply
            </button>
          </div>
        </div>
      )}

      <div className="overflow-auto" style={{ maxHeight: compact ? 360 : 560 }}>
        <table className="dense" data-testid="activity-records-table">
          <thead className="sticky top-0 bg-white z-10">
            <tr>
              <th></th>
              <th>Activity</th>
              <th>Site / Source Entity</th>
              <th className="text-right">Consumption</th>
              <th className="text-right">Emission Factor</th>
              <th className="text-right">tCO₂e</th>
              <th>Period</th>
              <th>Consumption date</th>
              <th>Uploaded</th>
              <th>Modified</th>
              <th>Status</th>
              {canWrite && <th></th>}
            </tr>
          </thead>
          <tbody>
            {loading && <Empty msg="Loading source data…" canWrite={canWrite} />}
            {!loading && data?.rows?.length === 0 && <Empty msg="No source records match the filters." canWrite={canWrite} />}
            {!loading && data?.rows?.map((r) => {
              const isOpen = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr data-testid={`activity-row-${r.id}`} className="hover:bg-surface/60">
                    <td>
                      <button
                        data-testid={`activity-expand-${r.id}`}
                        onClick={() => setExpanded(isOpen ? null : r.id)}
                        className="text-ink-soft hover:text-brand"
                        title="View gas composition & calculation"
                      >
                        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </td>
                    <td>
                      <div className="font-medium">{r.activity_label || r.activity_type}</div>
                      <div className="text-[10px] text-ink-soft font-mono">{r.activity_type}</div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <span>{r.source_entity}</span>
                        {r.entity_id && <span className="dot bg-emerald-500" title="Tagged to Site entity (row-by-row consolidation)"/>}
                      </div>
                    </td>
                    <td className="num">
                      {fmtNumber(r.consumption_value)} <span className="text-[10px] text-ink-soft">{r.consumption_unit}</span>
                    </td>
                    <td className="num">
                      <span className="font-mono">{r.emission_factor}</span>
                      <div className="text-[10px] text-ink-soft">{r.emission_factor_unit}</div>
                    </td>
                    <td className="num font-semibold">{fmtNumber(r.co2e_value)}</td>
                    <td className="text-xs">{r.period}</td>
                    <td className="text-xs font-mono text-ink-soft">{r.consumption_date}</td>
                    <td className="text-xs font-mono text-ink-soft">{r.uploaded_date}</td>
                    <td className="text-xs font-mono text-ink-soft">{r.modified_date}</td>
                    <td>
                      <span className={"pill " + (STATUS_PILL[r.record_status] || "")}>
                        {r.record_status?.replaceAll("_", " ")}
                      </span>
                    </td>
                    {canWrite && (
                      <td className="text-right whitespace-nowrap">
                        <button
                          data-testid={`activity-edit-${r.id}`}
                          onClick={() => setEditing(r)}
                          className="text-ink-soft hover:text-brand p-1"
                          title="Edit record"
                        ><Pencil size={12}/></button>
                        {canDelete && (
                          <button
                            data-testid={`activity-delete-${r.id}`}
                            onClick={async () => {
                              if (!window.confirm("Delete this activity record?")) return;
                              await api.delete(`/activity-records/${r.id}`);
                              await load();
                            }}
                            className="text-ink-soft hover:text-status-error p-1 ml-1"
                            title="Delete record"
                          ><Trash2 size={12}/></button>
                        )}
                      </td>
                    )}
                  </tr>
                  {isOpen && (
                    <tr data-testid={`activity-detail-${r.id}`}>
                      <td colSpan={canWrite ? 12 : 11} className="bg-surface/40 p-4">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-xs">
                          <div>
                            <div className="dlabel">Calculation</div>
                            <div className="mt-1 font-mono text-[12px] leading-relaxed">
                              {fmtNumber(r.consumption_value)} {r.consumption_unit}
                              <span className="text-ink-soft"> × </span>
                              {r.emission_factor} {r.emission_factor_unit}
                              <span className="text-ink-soft"> ÷ 1000 = </span>
                              <span className="font-semibold">{fmtNumber(r.co2e_value)} tCO₂e</span>
                            </div>
                            <div className="text-[11px] text-ink-soft mt-2">
                              Factor source: {r.emission_factor_source}
                            </div>
                          </div>
                          <div>
                            <div className="dlabel">GHG composition (tCO₂e)</div>
                            <div className="mt-2 space-y-1">
                              {Object.entries(r.gas_breakdown || {})
                                .filter(([, v]) => v > 0)
                                .map(([g, v]) => (
                                  <div key={g} className="flex items-center justify-between gap-2">
                                    <span className="font-mono w-12">{g}</span>
                                    <div className="flex-1 h-1.5 bg-surface rounded-full overflow-hidden">
                                      <div className="h-full bg-brand" style={{
                                        width: `${(v / r.co2e_value) * 100}%`
                                      }} />
                                    </div>
                                    <span className="font-mono w-20 text-right">{fmtNumber(v, 3)}</span>
                                  </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div className="dlabel">Audit trail</div>
                            <div className="mt-1 space-y-1 text-[11px]">
                              <div><span className="text-ink-soft">Uploaded by:</span> {r.uploaded_by}</div>
                              <div><span className="text-ink-soft">Modified by:</span> {r.modified_by}</div>
                              <div><span className="text-ink-soft">Record ID:</span> <span className="font-mono">{r.id.slice(0,8)}</span></div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {(creating || editing) && catalogue && (
        <ActivityRecordForm
          kpiId={kpiId}
          existing={editing}
          catalogue={catalogue}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={async () => {
            setCreating(false); setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function ActivityRecordForm({ kpiId, existing, catalogue, onClose, onSaved }) {
  const isEdit = !!existing;
  const allowedActivities = (catalogue.kpi_to_activities[kpiId] || catalogue.activities.map(a => a.activity_type));
  const activityOptions = catalogue.activities.filter((a) => allowedActivities.includes(a.activity_type));

  const initActivity = existing?.activity_type || activityOptions[0]?.activity_type;
  const initMeta = catalogue.activities.find((a) => a.activity_type === initActivity);

  const [form, setForm] = useState({
    kpi_id: kpiId,
    activity_type: initActivity,
    source_entity: existing?.source_entity || "",
    entity_id: existing?.entity_id || "",
    consumption_value: existing?.consumption_value ?? 0,
    consumption_unit: existing?.consumption_unit || initMeta?.consumption_unit || "",
    consumption_date: existing?.consumption_date || new Date().toISOString().slice(0, 10),
    period: existing?.period || derivePeriod(new Date()),
    emission_factor: existing?.emission_factor ?? initMeta?.default_factor ?? 0,
    emission_factor_unit: existing?.emission_factor_unit || initMeta?.factor_unit || "",
    emission_factor_source: existing?.emission_factor_source || initMeta?.default_source || "",
    record_status: existing?.record_status || "pending",
    note: existing?.note || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onActivityChange = (act) => {
    const meta = catalogue.activities.find((a) => a.activity_type === act);
    setForm((f) => ({
      ...f,
      activity_type: act,
      consumption_unit: meta?.consumption_unit || f.consumption_unit,
      emission_factor: meta?.default_factor ?? f.emission_factor,
      emission_factor_unit: meta?.factor_unit || f.emission_factor_unit,
      emission_factor_source: meta?.default_source || f.emission_factor_source,
    }));
  };

  const previewCo2e = useMemo(() => {
    const v = Number(form.consumption_value || 0);
    const f = Number(form.emission_factor || 0);
    return (v * f) / 1000;
  }, [form.consumption_value, form.emission_factor]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        entity_id: form.entity_id || null,
        consumption_value: Number(form.consumption_value),
        emission_factor: Number(form.emission_factor),
      };
      if (isEdit) {
        await api.put(`/activity-records/${existing.id}`, payload);
      } else {
        await api.post("/activity-records", payload);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="activity-form-modal">
      <div className="card w-full max-w-2xl bg-white max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="section-label">{isEdit ? "Edit activity record" : "Add activity record"}</div>
            <div className="text-xs text-ink-soft mt-0.5">{kpiId}</div>
          </div>
          <button onClick={onClose} className="btn btn-ghost" data-testid="activity-form-close"><X size={14}/></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="dlabel">Activity type</label>
              <select
                data-testid="af-activity-type"
                value={form.activity_type}
                onChange={(e) => onActivityChange(e.target.value)}
                disabled={isEdit}
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"
              >
                {activityOptions.map((a) => (
                  <option key={a.activity_type} value={a.activity_type}>{a.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="dlabel">Site (entity)</label>
              <select
                data-testid="af-entity-id"
                value={form.entity_id}
                onChange={(e) => {
                  const sel = (catalogue.sites || []).find((s) => s.id === e.target.value);
                  setForm((f) => ({ ...f, entity_id: e.target.value, source_entity: sel?.name || f.source_entity }));
                }}
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"
              >
                <option value="">— untagged (100% consolidated) —</option>
                {(catalogue.sites || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <div className="text-[10px] text-ink-soft mt-1">
                Tag the row with the Site it belongs to so consolidation applies the owning Company's factor.
              </div>
            </div>
          </div>

          <div>
            <label className="dlabel">Source entity / site (free text)</label>
            <input
              data-testid="af-source-entity"
              required
              value={form.source_entity}
              onChange={(e) => setForm({ ...form, source_entity: e.target.value })}
              placeholder="e.g. Manila Hub"
              className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="dlabel">Consumption value</label>
              <input
                data-testid="af-consumption-value"
                required type="number" step="any"
                value={form.consumption_value}
                onChange={(e) => setForm({ ...form, consumption_value: e.target.value })}
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm font-mono"
              />
            </div>
            <div>
              <label className="dlabel">Consumption unit</label>
              <input
                data-testid="af-consumption-unit"
                value={form.consumption_unit}
                onChange={(e) => setForm({ ...form, consumption_unit: e.target.value })}
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"
              />
            </div>
            <div>
              <label className="dlabel">Consumption date</label>
              <input
                data-testid="af-consumption-date"
                required type="date"
                value={form.consumption_date}
                onChange={(e) => setForm({ ...form, consumption_date: e.target.value, period: derivePeriod(new Date(e.target.value)) })}
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="dlabel">Emission factor</label>
              <input
                data-testid="af-emission-factor"
                required type="number" step="any"
                value={form.emission_factor}
                onChange={(e) => setForm({ ...form, emission_factor: e.target.value })}
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm font-mono"
              />
            </div>
            <div>
              <label className="dlabel">Factor unit</label>
              <input
                data-testid="af-factor-unit"
                value={form.emission_factor_unit}
                onChange={(e) => setForm({ ...form, emission_factor_unit: e.target.value })}
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"
              />
            </div>
            <div>
              <label className="dlabel">Period</label>
              <input
                data-testid="af-period"
                value={form.period}
                onChange={(e) => setForm({ ...form, period: e.target.value })}
                placeholder="2025-Q4"
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="dlabel">Factor source / reference</label>
            <input
              data-testid="af-factor-source"
              value={form.emission_factor_source}
              onChange={(e) => setForm({ ...form, emission_factor_source: e.target.value })}
              className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="dlabel">Record status</label>
              <select
                data-testid="af-status"
                value={form.record_status}
                onChange={(e) => setForm({ ...form, record_status: e.target.value })}
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"
              >
                {catalogue.statuses.map((s) => (
                  <option key={s} value={s}>{s.replaceAll("_", " ")}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="dlabel">Note (optional)</label>
              <input
                data-testid="af-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="w-full border border-border rounded-md px-3 py-2 mt-1 text-sm"
              />
            </div>
          </div>

          <div className="card-soft p-3 bg-emerald-50 border border-emerald-200">
            <div className="dlabel text-brand">Live calculation preview</div>
            <div className="font-mono text-sm mt-1" data-testid="af-co2e-preview">
              {fmtNumber(Number(form.consumption_value || 0))} {form.consumption_unit}
              <span className="text-ink-soft"> × </span>
              {form.emission_factor} {form.emission_factor_unit}
              <span className="text-ink-soft"> ÷ 1000 = </span>
              <span className="font-semibold">{fmtNumber(previewCo2e, 4)} tCO₂e</span>
            </div>
          </div>

          {err && <div className="text-status-error text-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button data-testid="af-submit" disabled={busy} type="submit" className="btn btn-primary">
              {busy ? "Saving…" : (isEdit ? "Save changes" : "Add record")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function derivePeriod(d) {
  const y = d.getFullYear();
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `${y}-Q${q}`;
}

function DateRange({ label, from, to, onFrom, onTo, testidPrefix }) {
  return (
    <div>
      <label className="dlabel">{label}</label>
      <div className="flex gap-1 mt-1">
        <input
          data-testid={`${testidPrefix}-from`}
          type="date" value={from} onChange={(e) => onFrom(e.target.value)}
          className="w-1/2 border border-border rounded-md px-2 py-1.5 outline-none focus:border-brand text-[11px]"
        />
        <input
          data-testid={`${testidPrefix}-to`}
          type="date" value={to} onChange={(e) => onTo(e.target.value)}
          className="w-1/2 border border-border rounded-md px-2 py-1.5 outline-none focus:border-brand text-[11px]"
        />
      </div>
    </div>
  );
}
